import { Stack, Text } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import type React from "react"
import * as Yup from "yup"
import { CartService, DocumentsService, OpenAPI } from "../client"
import PCInput from "../components/PCInput"
import ConsentCheckbox from "./ConsentCheckbox"
import DocxViewer from "./DocxViewer"
import SignButton from "./SignButton"

const SignatureFormSchema = Yup.object().shape({
  name: Yup.string().required("Signature is required"),
  consent: Yup.boolean()
    .oneOf([true], "Consent is required")
    .required("Consent is required"),
})

const SignatureFormInitialValues = {
  name: "",
  consent: false,
}

interface SignatureProps {
  orderId: string | null
}

const Signature: React.FC<SignatureProps> = ({ orderId }) => {
  const url = `${OpenAPI.BASE}/api/v1/documents/draft/${orderId}`
  const handleSubmit = async (
    values: typeof SignatureFormInitialValues,
    {
      setSubmitting,
      resetForm,
    }: {
      setSubmitting: (isSubmitting: boolean) => void
      resetForm: () => void
    },
  ) => {
    if (!orderId) {
      console.error("Order ID is required")
      setSubmitting(false)
      return
    }

    const signatureForm = {
      name: values.name,
      consent: values.consent,
    }

    try {
      const message = await DocumentsService.signDraftDocument({
        orderId,
        requestBody: signatureForm,
      })
      console.log(message)
      const paymentUrl = await CartService.checkout({ orderid: orderId })
      console.log(paymentUrl)
      window.parent.postMessage(
        {
          navigateTo: paymentUrl,
        },
        "*",
      )
      window.location.href = paymentUrl // Navigate to the external URL
    } catch (error) {
      console.error("Failed to sign document:", error)
    } finally {
      setSubmitting(false)
      resetForm()
    }
  }

  if (!orderId) {
    return (
      <>
        <Text>Error: Order ID is required.</Text>
      </>
    )
  }

  return (
    <>
      <h1>Review and Sign</h1>
      <DocxViewer url={url} />
      <hr />
      <Formik
        initialValues={SignatureFormInitialValues}
        validationSchema={SignatureFormSchema}
        onSubmit={(values, actions) => {
          actions.setSubmitting(true)
          handleSubmit(values, actions)
        }}
      >
        {({ errors, touched, isSubmitting }) => (
          <Form>
            <Stack spacing={4}>
              <Field name="name">
                {({ field }: { field: any }) => (
                  <PCInput
                    {...field}
                    label="Type your name to sign"
                    placeholder="Your signature"
                    isInvalid={touched.name && !!errors.name}
                    error={errors.name}
                    value={field.value}
                  />
                )}
              </Field>
              <Field name="consent">
                {({ field }: { field: any }) => (
                  <ConsentCheckbox
                    {...field}
                    label="eSign Consent"
                    description="I consent to use electronic signatures and electronic records in connection with this transaction."
                    isInvalid={touched.consent && !!errors.consent}
                    error={errors.consent}
                    value={field.value}
                  />
                )}
              </Field>
              <SignButton
                type="submit"
                name="signButton"
                isLoading={isSubmitting}
              >
                Sign Document
              </SignButton>
            </Stack>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default Signature
